import React, { ReactNode, useState } from 'react'
import { AppSvg, Dropdown, newIcons } from '@web/walter-ui-kit'

import cn from 'classnames'
import { useAuth } from '@web/walter-lib/auth'
import styles from './style.module.css'
import HeaderMenuButton from './HeaderMenuButton'
import { useCommonCMSConfig } from '~api'
import PageSearch from '~components/PageLayout/PageSearch'
import useDefaultHandleBack from '~hooks/useDefaultHandleBack'
import Logo from '~components/App/Layout/Logo'
import QuickMenu from '~components/App/Layout/NavigationMenu/QuickMenu'
import ThemeButton from '~components/App/Layout/NavigationMenu/buttons/ThemeButton'
import LocaleButton from '~components/App/Layout/NavigationMenu/buttons/LocaleButton'
import ReferralButton from '~components/App/Layout/NavigationMenu/buttons/ReferralButton'

export interface Props {
    title?: ReactNode
    rootPage?: boolean
    onBackClick?: () => void
    right?: ReactNode
    left?: ReactNode
    center?: ReactNode
    withSearch?: boolean
    showRightSection?: boolean
    isAuthPage?: boolean
    withBackButton?: boolean
}

// eslint-disable-next-line complexity,max-lines-per-function
function PageHeader(props: Props) {
    const { locales = [] } = useCommonCMSConfig().data ?? {}

    const { rootPage = true, isAuthPage = false, showRightSection, withBackButton = false, withSearch = true } = props

    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const { isAuth } = useAuth()

    const defaultHandleBack = useDefaultHandleBack()

    const handleBackClick = () => {
        if (props.onBackClick) {
            props.onBackClick()
            return
        }

        defaultHandleBack()
    }

    const rightSection = (
        <div className={cn(styles.rightSection, { [styles.fullWidth]: isSearchOpen })}>
            {!isAuthPage && isAuth && withSearch && (
                <div className={cn({ [styles.searchWrap]: isSearchOpen })}>
                    <PageSearch isOpenMobile={isSearchOpen} setOpenMobile={setIsSearchOpen} />
                </div>
            )}

            {(isAuthPage || !isAuth) && !isSearchOpen && locales.length >= 2 && (
                <div className={styles.langButton}>
                    <LocaleButton isIcon />
                </div>
            )}

            {(isAuthPage || !isAuth) && !isSearchOpen && (
                <div className={styles.themeButton}>
                    <ThemeButton isIcon />
                </div>
            )}

            {!isAuthPage && isAuth && !isSearchOpen && (
                <div className={styles.referralButton}>
                    <ReferralButton isIcon />
                </div>
            )}

            {!isSearchOpen && (
                <div className={cn(styles.profile, isAuth && styles.profileAuth)}>
                    <Dropdown dataPointer="dropdown_menu" placement="top-end" content={<QuickMenu isMobile />}>
                        {open => <HeaderMenuButton isOpen={open} isAuth={isAuth} />}
                    </Dropdown>
                </div>
            )}
        </div>
    )

    if (rootPage) {
        return (
            <div data-pointer="page_header" className={styles.rootPageWrap}>
                <div className={cn(styles.header, isAuthPage && styles.isAuthPageHeader)}>
                    {withBackButton && !isAuthPage && (
                        <div data-pointer="back_button" className={styles.backButton} onClick={handleBackClick}>
                            <AppSvg svgName={newIcons.ic24ArrowLeft} size={24} path="new-icons" />
                        </div>
                    )}

                    {!isAuthPage && (
                        <div data-pointer="title" className={cn(styles.title, props.left && styles.titleWithLeft)}>
                            {props.title}
                            {props.left}
                        </div>
                    )}

                    {!isSearchOpen && (
                        <div className={styles.logo}>
                            <Logo isAuthPage={isAuthPage} />
                        </div>
                    )}

                    {rightSection}
                </div>
            </div>
        )
    }
    return (
        <div data-pointer="page_header" className={styles.nestedPageWrap}>
            {withBackButton && (
                <div data-pointer="back_button" className={styles.backButton} onClick={handleBackClick}>
                    <AppSvg svgName={newIcons.ic24ArrowLeft} size={24} path="new-icons" />
                </div>
            )}
            <div data-pointer="title" className={styles.title}>
                {props.title}
            </div>
            {props.center && (
                <div
                    data-pointer="center"
                    className="absolute left-[50%] top-[50%] translate-[-50%] text-title-3-semibold"
                >
                    {props.center}
                </div>
            )}
            {props.right ? (
                <div data-pointer="right" className={styles.right}>
                    {props.right}
                </div>
            ) : (
                <div className={styles.rightPlaceholder} />
            )}
            {showRightSection ? <div className={styles.customRight}>{rightSection}</div> : undefined}
        </div>
    )
}

export default PageHeader
